import { apiRequest, apiUpload } from '@/lib/api-client';

type Announcement = {
  id: string;
  title_tr: string;
  title_en: string;
  content_tr: string | null;
  content_en: string | null;
  image_url: string | null;
  published: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
};

type NewAnnouncement = Omit<Announcement, 'id' | 'created_at' | 'updated_at'>;
type UpdateAnnouncement = Partial<NewAnnouncement>;

export async function getAllAnnouncements(publishedOnly = false): Promise<Announcement[]> {
  const endpoint = publishedOnly ? '/announcements/index.php' : '/announcements/index.php?all=1';
  return apiRequest<Announcement[]>(endpoint);
}

export async function getPublishedAnnouncements(): Promise<Announcement[]> {
  return getAllAnnouncements(true);
}

export async function getAnnouncementById(id: string): Promise<Announcement | null> {
  try {
    return await apiRequest<Announcement>(`/announcements/${id}`);
  } catch (error) {
    return null;
  }
}

export async function getAnnouncement(id: string): Promise<Announcement | null> {
  return getAnnouncementById(id);
}

export async function createAnnouncement(announcement: NewAnnouncement): Promise<Announcement> {
  return apiRequest<Announcement>('/announcements/index.php', {
    method: 'POST',
    body: JSON.stringify(announcement),
  });
}

export async function updateAnnouncement(id: string, updates: UpdateAnnouncement): Promise<Announcement> {
  return apiRequest<Announcement>(`/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAnnouncement(id: string): Promise<void> {
  await apiRequest(`/announcements/${id}`, {
    method: 'DELETE',
  });
}

export async function uploadAnnouncementImage(file: File): Promise<string> {
  return apiUpload(file, 'announcements/');
}

// Alias functions for compatibility with old naming
export async function getAnnouncements(publishedOnly = false): Promise<Announcement[]> {
  return getAllAnnouncements(publishedOnly);
}

export async function addAnnouncement(announcement: NewAnnouncement): Promise<Announcement> {
  return createAnnouncement(announcement);
}

export async function editAnnouncement({ id, updates }: { id: string; updates: UpdateAnnouncement }): Promise<Announcement> {
  return updateAnnouncement(id, updates);
}

export async function removeAnnouncement(id: string): Promise<void> {
  return deleteAnnouncement(id);
}
